import React, { useContext, useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import { Whisper, Popover } from "rsuite";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import { DateTime } from "luxon";
import { GlassCard, SectionRibbon, colors } from "../../../../ui";

export const EventHistoryFC = () => {
  const triggerRef = useRef();
  const { darkMode } = useContext(DarkModeContext);
  const { getAccessTokenSilently } = useAuth0();
  const { id, dataModel } = useParams();
  const [eventHistory, setEventHistory] = useState([]);
  const [getEventHistoryError, setEventHistoryError] = useState(false);
  const [loadingEventHistory, setLoadingEventHistory] = useState(true);

  const getEventHistory = async () => {
    // console.log(`getMachineMetadata`);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/system/event-history/${id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEventHistory(response.data[0]);
    } catch (error) {
      console.error(error);
      setEventHistoryError(true);
    }
    setLoadingEventHistory(false);
  };

  useEffect(() => {
    getEventHistory();
  }, []);

  const formatFieldName = (fieldName) => {
    const fieldMap = {
      tube_serial_no: "Tube Serial #",
      tube_type: "Tube Type",
      scan_seconds: "Tube Usage",
      software_version: "Software Version",
      power_on: "Power On",
      system_scan_seconds: "System Scan Seconds",
    };
    return fieldMap[fieldName] || fieldName;
  };

  const formatFieldValue = (fieldName, value) => {
    if (fieldName === "scan_seconds" || fieldName === "system_scan_seconds") {
      return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} seconds`;
    }
    return value;
  };

  return !loadingEventHistory && eventHistory.length > 0 ? (
    <EventHistoryContainer>
      <GlassCard animated visible={!loadingEventHistory} size="small">
        <EventGrid>
          {eventHistory?.map((event) => {
            const tooltipContent = (
              <Popover title="Last Read">
                {DateTime.fromISO(event?.host_datetime).toFormat("ff")}
              </Popover>
            );

            return (
              <Whisper
                placement="top"
                speaker={tooltipContent}
                trigger="hover"
                key={event?.name}
              >
                <EventItem>
                  <EventLabel>{formatFieldName(event?.name)}</EventLabel>
                  <EventValue>
                    {formatFieldValue(event?.name, event?.value)}
                  </EventValue>
                </EventItem>
              </Whisper>
            );
          })}
        </EventGrid>
      </GlassCard>
    </EventHistoryContainer>
  ) : null;
};

// Modern Styled Components
const EventHistoryContainer = styled.div`
  min-width: 25%;
  margin: 0 auto;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 6px;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
`;

const EventItem = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  min-height: 70px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.08) 100%
    );
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media screen and (max-width: 480px) {
    padding: 6px 4px;
    min-height: 60px;
  }
`;

const EventLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  color: ${colors.neutral[600]};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media screen and (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 4px;
  }
`;

const EventValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  color: ${colors.primary.main};
  word-break: break-word;

  @media screen and (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.8rem;
  }
`;
